import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Footer, Header, LangSwitcher, StyledLayout } from "./Layout.styles";
import { useContext, useEffect, useRef } from "react";
import { useApp } from "contexts/AppProvider";
import { useIntl } from "react-intl";
import { Button } from "antd";
import { LanguageContext } from "contexts/LangProvider";
import { LOCALES } from "i18n/messages";

export const Layout = () => {
  const { activeTimeslot, mainColor, supportEnglish } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const layoutRef = useRef<HTMLDivElement>(null);
  const isPersonalFlow =
    location.pathname === "/personal" || new URLSearchParams(location.search).get("personal") === "1";

  useEffect(() => {
    if (!activeTimeslot && location.pathname !== "/personal") {
      navigate(`/${location.search}`);
    }
  }, [activeTimeslot, location.search, location.pathname]);

  const { formatMessage } = useIntl();
  const { selectLanguage, locale } = useContext(LanguageContext);

  useEffect(() => {
    const layoutElement = layoutRef.current;
    const appElement = layoutElement?.closest<HTMLElement>(".App--personal");
    if (!appElement || !isPersonalFlow || window.parent === window) return;

    let animationFrame: number | null = null;
    let lastHeight = 0;

    const emitHeight = () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const height = Math.ceil(Math.max(appElement.getBoundingClientRect().height, appElement.scrollHeight));

        if (height === lastHeight) return;
        lastHeight = height;
        window.parent.postMessage({ type: "MYMEDCAL_WIDGET_HEIGHT", height }, "*");
      });
    };

    const resizeObserver = new ResizeObserver(emitHeight);
    resizeObserver.observe(appElement);
    window.addEventListener("resize", emitHeight);
    emitHeight();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", emitHeight);
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
    };
  }, [isPersonalFlow, location.pathname, location.search]);

  return (
    <StyledLayout ref={layoutRef} personalFlow={isPersonalFlow}>
      {!isPersonalFlow && (
        <Header mainColor={mainColor}>{formatMessage({ defaultMessage: "Umów wizytę", id: "LOAXb+" })}</Header>
      )}

      <div
        className={`layout-content${isPersonalFlow ? " layout-content--personal" : ""}`}
        style={{
          width: "100%",
          padding: "0 10px 40px",
          marginTop: 0,
        }}
      >
        {supportEnglish && (
          <LangSwitcher justify="flex-end" align="center">
            <Button
              onClick={() => selectLanguage(LOCALES.POLISH)}
              type="text"
              style={{ fontWeight: locale === LOCALES.POLISH ? "bold" : "normal" }}
            >
              PL
            </Button>
            <div />
            <Button
              onClick={() => selectLanguage(LOCALES.ENGLISH)}
              type="text"
              style={{ fontWeight: locale === LOCALES.ENGLISH ? "bold" : "normal" }}
            >
              EN
            </Button>
          </LangSwitcher>
        )}

        <Outlet />
      </div>
      <Footer mainColor={mainColor}>
        Powered by{" "}
        <a href="https://mymedcal.pl" target="_blank" rel="noreferrer">
          <strong>MyMedCal</strong>
        </a>
      </Footer>
    </StyledLayout>
  );
};
